import { create } from 'zustand';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  init: () => void;
  set: (t: Theme) => void;
  toggle: () => void;
}

const KEY = 'myfood:theme';

function apply(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
  // also update meta theme-color so iOS status bar matches
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', theme === 'dark' ? '#140F0A' : '#F97316');
}

export const useThemeStore = create<ThemeState>((set) => ({
  theme: 'light',
  init: () => {
    const stored = (typeof localStorage !== 'undefined' && localStorage.getItem(KEY)) as Theme | null;
    const sysDark =
      typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme: Theme = stored ?? (sysDark ? 'dark' : 'light');
    apply(theme);
    set({ theme });

    // Track system preference if user hasn't explicitly chosen
    if (!stored && typeof window !== 'undefined') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', (e) => {
        const t: Theme = e.matches ? 'dark' : 'light';
        apply(t);
        set({ theme: t });
      });
    }
  },
  set: (t) => {
    localStorage.setItem(KEY, t);
    apply(t);
    set({ theme: t });
  },
  toggle: () => {
    const next: Theme = (localStorage.getItem(KEY) as Theme) === 'dark' ? 'light' : 'dark';
    localStorage.setItem(KEY, next);
    apply(next);
    set({ theme: next });
  },
}));
